/**
 * Provides the `getFormattedDuration` utility function for formatting a duration (a number and a time unit) into a localized, human-readable string, handling singular/plural forms and offering an optional short format, and its associated `GetFormattedDurationUnit` type.
 * @module getFormattedDuration
 */
// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';
// Import i18n functions
import { __, _n, sprintf } from '@wordpress/i18n';
/**
 * Formats a duration (a number and a time unit) into a localized, human-readable string, handling singular/plural forms and offering an optional short format.
 *
 * **Behavior:**
 * - Takes a time `unit` (e.g., 'minutes', 'hours'), a `number` representing the quantity, and an optional `short` boolean flag.
 * - Ensures the input `number` is non-negative; negative numbers are treated as 0.
 * - If `short` is `true`, it returns an abbreviated format (e.g., "5 min", "1 h", "3 d") based on the [Microsoft Writing Style Guide](https://learn.microsoft.com/en-us/style-guide/a-z-word-list-term-collections/term-collections/date-time-terms) abbreviations.
 * - If `short` is `false` (default), it returns the full, localized unit name (e.g., "5 minutes", "1 hour", "3 days").
 * - Throws an `Error` if an unrecognized `unit` is provided.
 *
 * @since 6.5 Initial implementation.
 * @param {GetFormattedDurationUnit} unit - The unit of time. Must be one of 'seconds', 'minutes', 'hours', 'days', 'weeks', 'months', 'years'.
 * @param {number} number - The quantity of the time unit. Interpreted as 0 if negative.
 * @param {boolean} [short=false] - If true, returns an abbreviated format (e.g., "5 min"). Defaults to `false`.
 * @return {string} The formatted, localized duration string (e.g., "5 minutes" or "1 h").
 * @throws {Error} If an invalid `unit` is provided (e.g., 'millennia').
 *
 * @example Importing the getFormattedDuration utility
 * ```typescript
 * import { getFormattedDuration } from '@automator/shared/utils/get-formatted-duration';
 * ```
 *
 * @example Basic plural formatting
 * Returns the full, plural, localized string for 5 minutes.
 * ```typescript
 * getFormattedDuration( 'minutes', 5 );
 * // => '5 minutes'
 * ```
 *
 * @example Basic singular formatting
 * Returns the full, singular, localized string for 1 hour.
 * ```typescript
 * getFormattedDuration( 'hours', 1 );
 * // => '1 hour'
 * ```
 *
 * @example Short format (plural count)
 * Returns the abbreviated string for 10 days.
 * ```typescript
 * getFormattedDuration( 'days', 10, true );
 * // => '10 d'
 * ```
 *
 * @example Short format (singular count)
 * Returns the abbreviated string for 1 year.
 * ```typescript
 * getFormattedDuration( 'years', 1, true );
 * // => '1 yr'
 * ```
 */
export function getFormattedDuration(unit, number, short = false) {
    // Ensure number is non-negative, default to 0 if not valid
    const count = Math.max(0, number);
    switch (unit) {
        case 'seconds':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s sec', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s second', '%1$s seconds', count, 'uncanny-automator')), count);
        case 'minutes':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s min', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s minute', '%1$s minutes', count, 'uncanny-automator')), count);
        case 'hours':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s h', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s hour', '%1$s hours', count, 'uncanny-automator')), count);
        case 'days':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s d', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s day', '%1$s days', count, 'uncanny-automator')), count);
        case 'weeks':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s wk', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s week', '%1$s weeks', count, 'uncanny-automator')), count);
        case 'months':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s mo', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s month', '%1$s months', count, 'uncanny-automator')), count);
        case 'years':
            if (short) {
                return sprintf(
                /* translators: 1. A number */
                escapeHTML(__('%1$s yr', 'uncanny-automator')), count);
            }
            return sprintf(
            /* translators: 1. A number */
            escapeHTML(_n('%1$s year', '%1$s years', count, 'uncanny-automator')), count);
        default:
            // Ensure exhaustive check with 'never' type
            const exhaustiveCheck = unit;
            throw new Error(`Invalid duration unit provided: ${exhaustiveCheck}`);
    }
}
;
